import React, { useState } from "react";
import IconComponent from "../../../../components/Icon/IconComponent";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import { ICON_LABELS, fontWeight } from "../../../../utils/constants/UI";
import { BORDER, GREY, SUCCESS } from "../../../../utils/constants/colors";
import EmailSentList from "./EmailSentList";
import SourceTypeChip from "./SourceTypeChip";

// One campaign = one row in the Emails tab, with its steps and the contact's replies inside it.
// The backend sends `thread` already ordered oldest-first, so the conversation reads top-down
// while the outer list stays newest-first.
//
// The items inside are rendered by the SAME EmailSentList used when they were separate rows, so
// engagement chips, reply bodies and comments keep working here without being reimplemented.
const CampaignThreadCard = ({
  emailData,
  contactDetails,
  getEmailLogList = () => {},
  expanded = false,
  openSendEmailModal,
  setOpenSendEmailModal,
  setClickedEmail,
  setReplyType,
  isPreScheduled = false,
}) => {
  const [open, setOpen] = useState(!!expanded);
  const thread = Array.isArray(emailData?.thread) ? emailData.thread : [];
  const sent = Number(emailData?.sent_count) || 0;
  const scheduled = Number(emailData?.scheduled_count) || 0;
  const replies = Number(emailData?.replies_count) || 0;

  // Engagement has to be readable WITHOUT opening the thread. The chips live on each mail inside,
  // and collapsing the campaign into one row buried all of them one click deep — which defeated the
  // whole point of showing them. So the header carries the roll-up: how many of this campaign's
  // mails a PERSON opened or clicked (`=== true` only, so a scanner's pixel hit never counts).
  const engagementRollup = thread.reduce(
    (acc, i) => {
      const e = i?.engagement;
      if (!e) return acc;
      if (e.opened === true) acc.opened += 1;
      if (e.clicked === true) acc.clicked += 1;
      if (e.bounced) acc.bounced += 1;
      return acc;
    },
    { opened: 0, clicked: 0, bounced: 0 },
  );
  const rollupChips = [
    engagementRollup.opened
      ? { label: `${engagementRollup.opened} opened`, bg: "#F1F1F2", color: "#5F5F61", border: "#E5E5E6" }
      : null,
    engagementRollup.clicked
      ? { label: `${engagementRollup.clicked} clicked`, bg: "#EAF1FF", color: "#2C5DE5", border: "#C9D9FC" }
      : null,
    engagementRollup.bounced
      ? { label: `${engagementRollup.bounced} bounced`, bg: "#FDECEC", color: "#B42318", border: "#F8CDCB" }
      : null,
  ].filter(Boolean);

  // Counts, not prose: a rep scanning the list wants to know whether this conversation went
  // anywhere. A reply is the part worth colouring.
  // "3 scheduled" was a lie on a campaign still in Ready To Run: those steps exist but no send
  // window has been set, so the count was describing rows, not reality. When the parent campaign
  // has not been scheduled yet, say so instead of borrowing the word.
  const summary = [
    sent ? `${sent} ${sent === 1 ? "mail" : "mails"} sent` : null,
    scheduled
      ? isPreScheduled
        ? `${scheduled} ${scheduled === 1 ? "step" : "steps"} not scheduled yet`
        : `${scheduled} scheduled`
      : null,
  ]
    .filter(Boolean)
    .join(" · ") || "No mails yet";

  return (
    <div
      style={{
        border: `1px solid ${BORDER.DEFAULT}`,
        borderRadius: "8px",
        marginBottom: "12px",
        background: "#FFFFFF",
      }}
    >
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "12px 14px",
          cursor: "pointer",
        }}
      >
        <IconComponent
          iconLabel={open ? ICON_LABELS.ACTION_ARROW : ICON_LABELS.KEYBOARD_ARROW_RIGHT}
          color={GREY.TERTIARY}
          fontSize={"0.85rem"}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <TypographyInline
            label={emailData?.subject || "Campaign"}
            size={"sm"}
            fontWeight={fontWeight.SEMIBOLD}
            color={GREY.PRIMARY}
            isEllipses
          />
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "3px" }}>
            <span style={{ fontSize: "12px", color: GREY.SECONDARY }}>{summary}</span>
            {replies ? (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  height: "18px",
                  padding: "0 8px",
                  borderRadius: "999px",
                  fontSize: "11px",
                  fontWeight: 600,
                  background: SUCCESS.W_50,
                  color: SUCCESS.W_700,
                  border: `1px solid ${SUCCESS.W_100}`,
                }}
              >
                {replies === 1 ? "1 reply" : `${replies} replies`}
              </span>
            ) : null}
            {rollupChips.map((chip) => (
              <span
                key={chip.label}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  height: "18px",
                  padding: "0 8px",
                  borderRadius: "999px",
                  fontSize: "11px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  background: chip.bg,
                  color: chip.color,
                  border: `1px solid ${chip.border}`,
                }}
              >
                {chip.label}
              </span>
            ))}
          </div>
        </div>
        {/* Right side, beside the toggle - NOT above the subject, and using the shared
            SourceTypeChip so the colour matches the same pill everywhere else in the app
            (Marketing Campaign blue, Sales Sequence purple). Hand-rolling this chip is what
            made the header purple while the rows inside it were blue. */}
        <SourceTypeChip recordType={emailData?.record_type_for_crm} />
      </div>

      {open ? (
        <div style={{ borderTop: `1px solid ${BORDER.DEFAULT}`, padding: "6px 10px 10px" }}>
          {thread.map((item, i) => (
            <EmailSentList
              key={`thr-${item?._id || i}`}
              id={`thr-${i}`}
              emailData={item}
              contactDetails={contactDetails}
              getEmailLogList={getEmailLogList}
              expanded={false}
              openSendEmailModal={openSendEmailModal}
              setOpenSendEmailModal={setOpenSendEmailModal}
              setClickedEmail={setClickedEmail}
              setReplyType={setReplyType}
              isPreScheduled={isPreScheduled}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default CampaignThreadCard;
